import { Button as MaterialButton } from '@mui/material';
import React, { forwardRef, useMemo } from 'react';

import { LoadingDots } from '../LoadingDots/LoadingDots';
import type { ButtonProps } from './Button.types';

const DEFAULT_VARIANT = 'standard';
const DEFAULT_COLOR = 'primary';
const DEFAULT_SIZE = 'medium';
const DEFAULT_LOADING_POSITION = 'center';

/**
 * The Button
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/form-komponenten/button]
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      size = DEFAULT_SIZE,
      variant = DEFAULT_VARIANT,
      color = DEFAULT_COLOR,
      fullWidth = false,
      loading,
      loadingPosition = DEFAULT_LOADING_POSITION,
      startIcon,
      endIcon,
      disabled,
      ...props
    },
    ref
  ) => {
    const sx: ButtonProps['sx'] = useMemo(() => {
      if (fullWidth === 'text') {
        return Array.isArray(props.sx)
          ? [
              {
                maxWidth: 'text',
                left: '50%',
                transform: 'translate(-50%, 0px)',
              },
              ...props.sx,
            ]
          : {
              maxWidth: 'text',
              left: '50%',
              transform: 'translate(-50%, 0px)',
              ...props.sx,
            };
      }
      return props.sx;
    }, [fullWidth, props.sx]);

    return (
      <MaterialButton
        ref={ref}
        fullWidth={!!fullWidth}
        size={size || DEFAULT_SIZE}
        variant={variant || DEFAULT_VARIANT}
        color={color || DEFAULT_COLOR}
        component="button"
        disabled={loading || disabled}
        startIcon={loading && loadingPosition === 'start' ? <LoadingDots /> : startIcon}
        endIcon={loading && loadingPosition === 'end' ? <LoadingDots /> : endIcon}
        {...props}
        sx={sx}
      >
        {loading && loadingPosition === 'center' ? <LoadingDots /> : children}
      </MaterialButton>
    );
  }
);

Button.displayName = 'Button';
